import { RouteWithGates } from '../api/routes';
import { connectedGroups, referenceLatitude, routeAnchor, routeFirstPoint } from './routeGeometry';
import { ROUTE_MERGE_MAX_DISTANCE_M } from './routeMergeCandidates';

export type RouteClusterGroup = {
  clusterId: number;
  clusterName: string;
  routes: RouteWithGates[];
};

/**
 * How close two routes must come to be shown at the same place. Five times
 * the merge limit: a short and a long section of one road are the same place
 * even when they are too far apart to merge, and the cluster is where the
 * merge button lives, so it has to hold everything a merge could want.
 */
const ROUTE_CLUSTER_MAX_DISTANCE_M = 5 * ROUTE_MERGE_MAX_DISTANCE_M;

/**
 * Group routes by where they are. Two routes share a cluster when their
 * sections come within ROUTE_CLUSTER_MAX_DISTANCE_M of each other, directly
 * or through other routes — so a long road with several sections along it is
 * one place, and one road never splits into two groups the way the old
 * latitude sweep split it. Clusters run south to north, routes within one
 * likewise; routes with no position sit together in a last cluster. A cluster
 * is named after the most common address among its routes.
 */
export function clusterRoutes(routes: RouteWithGates[]): RouteClusterGroup[] {
  if (!routes.length) return [];

  const lat0 = referenceLatitude(routes);
  const anchors = routes.map((r) => routeAnchor(r, lat0));
  const { groups } = connectedGroups(anchors, ROUTE_CLUSTER_MAX_DISTANCE_M);

  const latitude = (i: number) => routeFirstPoint(routes[i])?.[0] ?? Infinity;
  const ordered = groups
    .map((members) => [...members].sort((a, b) => latitude(a) - latitude(b)))
    .sort((g1, g2) => latitude(g1[0]) - latitude(g2[0]));
  const unplaced = routes.map((_, i) => i).filter((i) => anchors[i] === null);
  if (unplaced.length) ordered.push(unplaced);

  return ordered.map((members, index) => {
    const routesInCluster = members.map((i) => routes[i]);
    const addresses = routesInCluster.map((r) => r.address).filter(Boolean);
    return {
      clusterId: index + 1,
      clusterName: mostFrequent(addresses) || 'Unknown Location',
      routes: routesInCluster,
    };
  });
}

function mostFrequent(arr: string[]): string {
  const counts: Record<string, number> = {};
  let best = '';
  let max = 0;
  for (const v of arr) {
    counts[v] = (counts[v] || 0) + 1;
    if (counts[v] > max) {
      max = counts[v];
      best = v;
    }
  }
  return best;
}
